import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthQuery } from '@app/auth/auth.query';
import { TokenService } from '@app/@core/services/token.service';
import { environment } from '@env/environment';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private router: Router | null = null;

  constructor(
    private authQuery: AuthQuery,
    private tokenService: TokenService,
    private injector: Injector
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Don't intercept refresh token requests to avoid infinite loops
    if (request.url.includes('/auth/refresh')) {
      return next.handle(request);
    }

    const isProtectedApiUrl = request.url.includes('/v5/') || request.url.includes('/api/v5/');
    
    if (isProtectedApiUrl) {
      // Check if token needs refresh before making request
      if (this.tokenService.shouldRefreshToken()) {
        return this.handleTokenRefresh(request, next);
      }
      
      if (this.authQuery.access_token) {
        request = request.clone({
          setHeaders: {
            Authorization: `Bearer ${this.authQuery.access_token}`,
          },
        });
      }
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && isProtectedApiUrl) {
          return this.handleTokenRefresh(request, next);
        }
        return throwError(error);
      })
    );
  }

  private handleTokenRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.tokenService.refreshToken().pipe(
        switchMap((response: any) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(response.access_token);
          
          // Retry the original request with new token
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${response.access_token}`,
            },
          });
          
          return next.handle(request);
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          
          // If refresh failed with 401, redirect to login
          if (error.status === 401) {
            this.redirectToLogin();
          }
          
          return throwError(error);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
            },
          });
          return next.handle(request);
        })
      );
    }
  }

  private redirectToLogin(): void {
    // Lazy inject Router to avoid circular dependency
    if (!this.router) {
      this.router = this.injector.get(Router);
    }
    
    // Only redirect if not already on login page
    if (this.router && !this.router.url.includes('/login')) {
      console.log('TokenService: Redirecting to login due to failed refresh');
      this.router.navigate(['/login'], {
        queryParams: { 
          redirect: this.router.url 
        },
        replaceUrl: false
      });
    }
  }
}
